import { useState, useEffect } from 'react';
import { useOpsAuth } from '@/ops/OpsAuthContext';
import { Link } from 'react-router-dom';
import { Users, UserCheck, UserX, Layers, ArrowUpRight, Loader2, PlugZap, Globe } from 'lucide-react';

const HEALTH = {
  ok:       { dot: '#34D399', label: 'All systems operational' },
  degraded: { dot: '#FBBF24', label: 'Partial degradation' },
  down:     { dot: '#F87171', label: 'Outage detected' },
};

const HealthBadge = () => {
  const { opsAxios, OPS_API } = useOpsAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    let active = true;
    const fetchHealth = () => opsAxios({ method: 'GET', url: `${OPS_API}/system/health` })
      .then(r => { if (active) setData(r.data); }).catch(() => {});
    fetchHealth();
    const id = setInterval(fetchHealth, 60000);  // live refresh every 60s
    return () => { active = false; clearInterval(id); };
  }, [opsAxios, OPS_API]);
  const h = HEALTH[data?.overall] || null;
  const alerting = data && data.overall !== 'ok';
  return (
    <Link to="/ops/system" data-testid="ops-health-badge"
      className={`ops-card ops-card-hover flex items-center gap-2.5 px-3.5 h-10 ${alerting ? 'ops-flash' : ''}`}
      style={{ borderRadius: 8, borderColor: alerting ? h?.dot : undefined }}>
      {!data ? (
        <Loader2 size={13} className="animate-spin" style={{ color: 'var(--ops-muted)' }} />
      ) : (
        <span className={`ops-dot ${data.overall === 'ok' ? 'ops-ping' : ''}`} style={{ background: h?.dot, color: h?.dot }} />
      )}
      <span className="text-xs font-medium hidden sm:inline" style={{ color: data ? h?.dot : 'var(--ops-muted)' }}>
        {data ? h?.label : 'Checking…'}
      </span>
      {data && (
        <span className="ops-mono text-[10px]" style={{ color: 'var(--ops-muted)' }}>
          {data.summary.ok}/{data.checks.length}
        </span>
      )}
    </Link>
  );
};

const KPI = ({ label, value, icon: Icon, loading }) => (
  <div className="ops-card ops-card-hover p-5">
    <div className="flex items-start justify-between">
      <p className="ops-overline">{label}</p>
      <Icon size={15} style={{ color: 'var(--ops-muted)' }} />
    </div>
    {loading ? <div className="ops-skel h-8 w-16 mt-3" /> : <p className="ops-kpi-value mt-3">{value}</p>}
  </div>
);

const ConnectionHealthWidget = () => {
  const { opsAxios, OPS_API } = useOpsAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    opsAxios({ method: 'GET', url: `${OPS_API}/connection-health` })
      .then(r => setData(r.data)).catch(() => {});
  }, [opsAxios, OPS_API]);

  const trend = data?.trend || [];
  const max = Math.max(1, ...trend.map(t => t.needs_reconnect || 0));
  const needs = data?.needs_reconnect ?? null;
  const alerting = (needs ?? 0) > 0;

  return (
    <div className="ops-card p-5" data-testid="ops-connection-health-widget">
      <div className="flex items-start justify-between">
        <p className="ops-overline">Connections needing reconnect</p>
        <PlugZap size={15} style={{ color: alerting ? '#F87171' : 'var(--ops-muted)' }} />
      </div>
      {data === null ? (
        <div className="ops-skel h-8 w-16 mt-3" />
      ) : (
        <div className="flex items-end justify-between gap-4 mt-3">
          <div>
            <p className="ops-kpi-value" style={{ color: alerting ? '#F87171' : undefined }} data-testid="ops-needs-reconnect-count">{needs}</p>
            <p className="ops-sub text-xs mt-1">of {data.connected_total} connected accounts</p>
          </div>
          {trend.length > 0 ? (
            <div className="flex items-end gap-1 h-12" data-testid="ops-connection-trend" title="Daily needs-reconnect (last 30d)">
              {trend.map((t, i) => (
                <div key={t.date || i}
                  title={`${t.date}: ${t.needs_reconnect}`}
                  style={{
                    width: 5,
                    height: `${Math.max(6, Math.round(((t.needs_reconnect || 0) / max) * 48))}px`,
                    background: (t.needs_reconnect || 0) > 0 ? '#FBBF24' : 'var(--ops-border)',
                    borderRadius: 2,
                  }} />
              ))}
            </div>
          ) : (
            <p className="ops-sub text-xs">Trend builds daily</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function OpsDashboardPage() {
  const { opsAxios, OPS_API, opsUser } = useOpsAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    opsAxios({ method: 'GET', url: `${OPS_API}/dashboard` })
      .then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [opsAxios, OPS_API]);

  const s = data?.stats;
  const KPIS = [
    { label: 'Total Users', value: s?.total_users ?? '—', icon: Users },
    { label: 'Onboarded', value: s?.active_users ?? '—', icon: UserCheck },
    { label: 'Suspended', value: s?.suspended_users ?? '—', icon: UserX },
    { label: 'Action Cards', value: s?.total_action_cards ?? '—', icon: Layers },
  ];

  return (
    <div data-testid="ops-dashboard-page">
      <div className="mb-7 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="ops-h1">Dashboard</h1>
          <p className="ops-sub mt-1">Welcome back, {opsUser?.name} · <span className="ops-mono">{opsUser?.role?.replace('_', ' ')}</span></p>
        </div>
        <HealthBadge />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {KPIS.map(k => <KPI key={k.label} {...k} loading={loading} />)}
      </div>

      <div className="mb-8">
        <ConnectionHealthWidget />
      </div>

      <div className="ops-card p-5 mb-8" data-testid="ops-region-breakdown">
        <div className="flex items-start justify-between mb-3">
          <p className="ops-overline">Data residency — users by region</p>
          <Globe size={15} style={{ color: 'var(--ops-muted)' }} />
        </div>
        {loading ? (
          <div className="ops-skel h-8 w-40" />
        ) : (
          <div className="flex flex-wrap gap-3">
            {['ca-central-1', 'us-east-2', 'eu-central-1', 'unset'].map(r => (
              <div key={r} data-testid={`ops-region-${r}`} className="ops-card px-4 py-3" style={{ background: 'var(--ops-input)', minWidth: 130 }}>
                <p className="ops-kpi-value" style={{ fontSize: '1.5rem' }}>{data?.region_breakdown?.[r] ?? 0}</p>
                <p className="ops-mono text-[11px] mt-1" style={{ color: r === 'unset' ? 'var(--ops-muted)' : 'var(--ops-text-2)' }}>{r}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="ops-overline">Recent Signups</h2>
        <Link to="/ops/users" className="ops-mono text-xs flex items-center gap-1 hover:underline" style={{ color: 'var(--ops-accent)' }}>
          View all <ArrowUpRight size={12} />
        </Link>
      </div>

      <div className="ops-card overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-3">{Array(4).fill(0).map((_, i) => <div key={i} className="ops-skel h-8" />)}</div>
        ) : data?.recent_signups?.length === 0 ? (
          <div className="p-10 text-center ops-sub">No users yet</div>
        ) : (
          <table className="ops-table">
            <thead><tr>{['Name', 'Email', 'Joined', 'Status'].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {data?.recent_signups?.map(u => (
                <tr key={u.id}>
                  <td><Link to={`/ops/users/${u.id}`} className="font-medium hover:underline" style={{ color: 'var(--ops-text)' }}>{u.name}</Link></td>
                  <td className="ops-mono" style={{ color: 'var(--ops-text-2)' }}>{u.email}</td>
                  <td className="ops-mono" style={{ color: 'var(--ops-muted)' }}>{new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                  <td><span className={`ops-badge ${u.is_suspended ? 'ops-badge-danger' : 'ops-badge-success'}`}>{u.is_suspended ? 'Suspended' : 'Active'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
